import React, { useLayoutEffect, useRef, useState } from 'react';

import { Menu, MenuItem, Popper, CircularProgress } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import { Skeleton } from '@material-ui/lab';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import SkeletonLoader from 'components/SkeletonLoader';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectOrgData } from 'selectors/orgData';
import styled, { css } from 'styled-components';
import { Button } from 'styles/admin';
import {
  StyledBox,
  StyledPaper,
  StyledTypography,
  StyledGrid,
  StyledLinearProgress,
  StyledDivider,
  FlexContainer,
  StyledIconButton,
  ColoredCircularProgress,
  VerticallyAlignedFlexContainer,
} from 'styles/common';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import NotAvailable from 'assets/images/NotAvailable.png';

import { ANALYTICS_TEXTS, OBJECTIVE_ACCESS_PERMISSIONS } from 'constants/ProjectConstants';
import { convertDateToString4, getColorFromKey } from 'constants/helper';
import {
  BarGraphIcon,
  CheckIcon,
  DownloadArrowIcon,
  KrSvgIcon,
  ObjectiveSvgIcon,
  StyledHourGlassIcon,
  StyledIcon,
  TimelineGraphIcon,
} from 'constants/icons';
import theme from 'constants/theme';

import { Typography6, Typography2, Typography7 } from 'uiToolkit/Typography';

import { ToggleSwitch } from '../../components/ToggleSwitch';
import { useGoalAnalytics } from './goals/GoalAnalyticsContext';
import AddNewObjectiveOrGoalRow from './goals/GoalObjective/components/AddNewObjectiveOrGoalRow';
import { usePopperState } from './popperContext';
import { AbsCenterContainer } from './styles';

const PaddedContainer = ({ children, bgcolor = 'inherit', padding = '2rem', className = '' }) => (
  <StyledBox
    width="100%"
    padding={padding}
    display="flex"
    justifyContent="center"
    bgcolor={bgcolor}
    className={className}
  >
    {children}
  </StyledBox>
);

export const Loader = ({ bgcolor = 'inherit' }) => (
  <PaddedContainer bgcolor={bgcolor}>
    <SkeletonLoader skeletons={['100%', '100%']} />
  </PaddedContainer>
);

export const FilterBarLoader = ({ bgcolor = 'inherit' }) => (
  <PaddedContainer padding="0.5rem" bgcolor={bgcolor}>
    <Skeleton width="100%" />
  </PaddedContainer>
);

export const NoData = ({ infoText, className = '' }) => (
  <PaddedContainer className={className}>
    <StyledTypography variant="body1">{infoText}</StyledTypography>
  </PaddedContainer>
);

export const ErrorMessage = ({ errorText }) => (
  <PaddedContainer>
    <StyledTypography variant="body1" color="red">
      {errorText}
    </StyledTypography>
  </PaddedContainer>
);

export const EmptyBar = styled.div`
  width: ${(props) => props.width ?? '1.75rem'};
  height: ${(props) => props.height ?? '0.5rem'};
  border-radius: 0.5rem;
  margin-bottom: ${(props) => props.selected && '0.375rem'};
  background-color: ${(props) =>
    props.selected ? props.theme.palette.primary.main : props.theme.palette.text.secondaryLight};
`;

export const MeshPopper = () => {
  const { state: popperState } = usePopperState();
  const { open, anchorEl, component: children, placement } = popperState;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement || 'bottom'}
      transition
      modifiers={{
        flip: {
          enabled: true,
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport',
        },
      }}
      style={{ zIndex: 1000 }}
    >
      <StyledPaper>{children}</StyledPaper>
    </Popper>
  );
};

export const UnderLinedText = styled(StyledTypography)`
  ${(props) =>
    props.onClick &&
    css`
      &:hover {
        text-decoration: underline;
      }
    `};
`;

export const InfoText = ({ text, color = 'textSecondary' }) => (
  <StyledTypography variant="body1" color={color} texttransform="capitalize">
    {text}
  </StyledTypography>
);

export const CircularLoader = () => (
  <AbsCenterContainer>
    <CircularProgress />
  </AbsCenterContainer>
);

/* Chart components, used by max min charts */
export const MinMaxChartHeader = ({
  heading,
  subHeading,
  selectedIconId,
  updateSelectedIconId,
  iconsList,
  toggleSwitchHeight = null,
  display = null,
}) => (
  <StyledBox display="flex" alignItems="center" padding="1.5rem 2rem">
    <StyledBox flex={1}>
      <StyledTypography variant="h6" color="textPrimary" weight={600} bottomGutter={1}>
        {heading}
      </StyledTypography>
      <StyledTypography variant="body1" color="textSecondary">
        {subHeading}
      </StyledTypography>
    </StyledBox>
    <StyledBox marginLeft="auto">
      <ToggleSwitch
        selectedId={selectedIconId}
        onIconClick={updateSelectedIconId}
        iconsList={iconsList}
        height={toggleSwitchHeight}
        display={display}
      />
    </StyledBox>
  </StyledBox>
);

export const LabeledColoredCount = ({ labelText, count, bgColor, textColor, width = '5ch', className }) => (
  <StyledGrid className={className} container alignItems="center">
    <StyledTypography variant="body2" color="textSecondary" rightGutter={1}>
      {labelText}
    </StyledTypography>
    <ColoredBgText width={width} bgColor={bgColor} textColor={textColor} text={count} />
  </StyledGrid>
);

export const ColoredBgText = ({ width = '5ch', bgColor, textColor, text }) => (
  <StyledBox width={width} borderRadius="4px" padding="0.25rem 0" style={{ backgroundColor: bgColor }}>
    <StyledTypography variant="body1" color={textColor} weight={600} align="center">
      {text}
    </StyledTypography>
  </StyledBox>
);

export const ColoredDate = ({ bgColor, textColor, date }) => (
  <StyledGrid container justify="center">
    <StyledBox padding="0.5rem 2rem" bgcolor={bgColor} borderRadius={4}>
      <StyledTypography variant="body2" color={textColor} weight={600}>
        {convertDateToString4(date)}
      </StyledTypography>
    </StyledBox>
  </StyledGrid>
);

export const ProgressBarWithPercentage = ({ color, completionPercentage }) => (
  <>
    <StyledGrid container alignItems="center" justify="flex-end" item xs={9}>
      <StyledLinearProgress
        variant="determinate"
        color="primary"
        barColor={color}
        value={completionPercentage}
        fullWidth
        height="0.75rem"
      />
    </StyledGrid>
    <StyledGrid container alignItems="center" justify="flex-start" item xs={3}>
      <StyledTypography variant="body2" color={color}>
        {`${completionPercentage}%`}
      </StyledTypography>
    </StyledGrid>
  </>
);

export const DownLine = ({ level, inMeshList, index }) => (
  <DownLn level={level} id="dwnLine" inMeshList={inMeshList} index={index} />
);

export const Tline = ({ level, half, noHorizontal, forceOldValue, inMeshList, index }) => (
  <>
    <VerticalLine
      half={half}
      level={level}
      $forceOldValue={forceOldValue}
      inMeshList={inMeshList}
      index={index}
      id="niche"
    />
    {!noHorizontal && (
      <HorizontalLine level={level} $forceOldValue={forceOldValue} inMeshList={inMeshList} index={index} id="bagal" />
    )}
  </>
);

function getLeft(props) {
  if (props.inMeshList) {
    return `${26 * (props.index + 1) + 6 * props.index}px`;
  }
  return `${28 + (props.level - 1) * 124}px`;
}

function getDwnLnTop(props) {
  if (props.inMeshList) {
    return '42%';
  }
  return '50%';
}

const DownLn = styled.div`
  position: absolute;
  width: 1px;
  top: ${(props) => getDwnLnTop(props)};
  bottom: 0;
  background: #008ff061;
  ${(props) =>
    props.level &&
    css`
      left: ${getLeft(props)};
    `};
`;

function getTLineLeft(props) {
  if (props.inMeshList) {
    const rightGutter = props.index > 1 ? 6 * props.index : 6;
    return `${26 * props.index + rightGutter - 6}px`;
  }
  return `${28 + (props.level - 1) * 124}px`;
}

const VerticalLine = styled.div`
  position: absolute;
  width: 1px;
  top: 0;
  bottom: 0;
  background: ${(props) => `${props.theme.palette.primary.main}61`};
  ${(props) =>
    props.level &&
    css`
      left: ${getTLineLeft(props)};
    `};
  ${(props) =>
    props.half &&
    css`
      bottom: ${getVerticalLineBottom(props)};
    `};
`;

const HorizontalLine = styled.div`
  position: absolute;
  height: 1px;
  width: ${(props) => (props.inMeshList ? '14px' : '90px')};
  top: ${(props) => getHorizontalLineTop(props)};
  background: ${(props) => `${props.theme.palette.primary.main}61`};
  ${(props) =>
    props.level &&
    css`
      left: ${getTLineLeft(props)};
    `};
`;

function getVerticalLineBottom(props) {
  if (props.inMeshList) {
    return '70%';
  }
  if (!props.$forceOldValue) {
    return '70%';
  }
  return '50%';
}

function getHorizontalLineTop(props) {
  if (props.inMeshList) {
    return '30%';
  }
  if (!props.$forceOldValue) {
    return '30%';
  }
  return '50%';
}

// following container will retain its initially calculated width
// which for now is 100% of parent. Passing down text to it won't
// increase the width of this and subsequently parent container.
// currently using this with truncate to display ellipsis properly.
export const AbsoluteWidthTextContainer = ({ children }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState('100%');

  useLayoutEffect(() => {
    if (containerRef) {
      setWidth(`${containerRef.current.offsetWidth}px`);
    }
  }, [containerRef]);

  return (
    <StyledBox ref={containerRef} width={width}>
      {width !== '100%' && children}
    </StyledBox>
  );
};

// engagement onHover component for donut chart.

export const EngagementDonutChartHover = ({ data }) => (
  <StyledBox padding="1rem" width="40ch">
    <StyledGrid>
      <StyledTypography variant="body1" weight={600}>
        Score distribution
      </StyledTypography>
    </StyledGrid>
    <StyledDivider topGutter={2} bottomGutter={2} />
    <StyledGrid direction="column">
      {data.map(({ key, COUNT, COLOR }) => (
        <EngagementDonutChartHoverRow key={key} text={key} count={COUNT} color={COLOR} />
      ))}
    </StyledGrid>
  </StyledBox>
);

const EngagementDonutChartHoverRow = ({ text, count, color }) => (
  <StyledGrid container>
    <StyledGrid container item xs={7}>
      <StyledIcon size="header" color={getColorFromKey(color)} rightGutter={1}>
        <FiberManualRecordIcon />
      </StyledIcon>
      <StyledTypography variant="body1">{text}</StyledTypography>
    </StyledGrid>
    <StyledGrid container item xs={5} justify="flex-end">
      <StyledTypography variant="body1" color="textSecondary">
        {`${count} Employee`}
      </StyledTypography>
    </StyledGrid>
  </StyledGrid>
);

export const NoDataAvailable = ({
  showGoBack = null,
  onGoBack = null,
  createNewCohort = null,
  selectNewCohort = null,
  showAddNewObjectiveOrGoalRow = false,
  paginationProps = {},
  setIsOpenCreateGoalPanel = () => {},
  setIsGoalPanelOfCreatedObjective = () => {},
  setTimeboundDetailsOfCreatedObjective = () => {},
  src = NotAvailable,
}) => {
  // objective panel open/close states
  const [isObjectiveSidePanelOpen, setIsObjectiveSidePanel] = useState(false);

  const {
    data,
    dataCount,
    rowsPerPageOptions,
    rowsPerPage,
    pageNumber,
    setPageNumber,
    setRowsPerPage,
    setData,
    setAllData,
    updateRowDataModifiedForArray,
    addNewObjective,
  } = paginationProps;

  const { setNoData, setErrorData } = useGoalAnalytics();

  const { state: { currentState } = {} } = useGoalAnalytics();
  const { selectedTimeBoundPeriods } = currentState ?? {};

  return (
    <>
      {showAddNewObjectiveOrGoalRow && (
        <AddNewObjectiveOrGoalRow
          setIsOpenCreateObjectivePanel={setIsObjectiveSidePanel}
          setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
        />
      )}
      {isObjectiveSidePanelOpen && (
        <ObjectiveSidePanel
          open={isObjectiveSidePanelOpen}
          onCancel={() => setIsObjectiveSidePanel(false)}
          uuid={null}
          selectedTimeBoundPeriod={selectedTimeBoundPeriods[0]}
          objectiveTimeBoundId={null}
          addNewObjective={addNewObjective}
          rows={data}
          updateRowDataModifiedForArray={updateRowDataModifiedForArray}
          isAlignmentAtParentLevel
          setIsOpenCreateGoalPanel={(value) => {
            setIsOpenCreateGoalPanel(value);
            setIsGoalPanelOfCreatedObjective(value);
          }}
          setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
          setNoData={setNoData}
          source="Insights Page"
        />
      )}
      <StyledPaper elevation={0}>
        <StyledFlexContainer padding="5rem">
          <StyledImage src={src} alt="No data to display" />
          <Typography6>Nothing to see here!</Typography6>
          <Typography2 style={{ whiteSpace: 'break-spaces', textAlign: 'center' }}>
            {ANALYTICS_TEXTS.COHORT.NO_DATA}
          </Typography2>

          <StyledBox style={{ width: '100%' }} topGutter={3} flexcontainer alignitems="center" justifycontent="center">
            {showGoBack && (
              <Button blueOutline size="large" onClick={onGoBack}>
                <StyledTypography color="primary">Go back</StyledTypography>
              </Button>
            )}

            {createNewCohort && (
              <Button onClick={createNewCohort} blueOutline size="large">
                <StyledTypography color="primary">Create New Cohort</StyledTypography>
              </Button>
            )}

            <ButtonSpacer />
            {selectNewCohort && (
              <Button style={{ height: '2.5rem' }} size="large" blue rightGutter={3}>
                <StyledTypography color="white">Select New Cohort</StyledTypography>
              </Button>
            )}
          </StyledBox>
        </StyledFlexContainer>
      </StyledPaper>
    </>
  );
};

export const ErrorInData = ({ showGoBack = null, onGoBack = null, createNewCohort = null, selectNewCohort = null }) => (
  <StyledPaper elevation={0}>
    <StyledFlexContainer padding="5rem">
      <StyledImage src={NotAvailable} alt="No data to display" />
      <Typography6>Error in fetching data. Please try again later.</Typography6>
      <Typography2 style={{ whiteSpace: 'break-spaces', textAlign: 'center' }}>
        {ANALYTICS_TEXTS.COHORT.ERROR_MSG}
      </Typography2>

      <StyledBox style={{ width: '100%' }} topGutter={3} flexcontainer alignitems="center" justifycontent="center">
        {showGoBack && (
          <Button blueOutline size="large" onClick={onGoBack}>
            <StyledTypography color="primary">Go back</StyledTypography>
          </Button>
        )}

        {createNewCohort && (
          <Button onClick={createNewCohort} blueOutline size="large">
            <StyledTypography color="primary">Create New Cohort</StyledTypography>
          </Button>
        )}

        <ButtonSpacer />
        {selectNewCohort && (
          <Button style={{ height: '2.5rem' }} size="large" blue rightGutter={3}>
            <StyledTypography color="white">Select New Cohort</StyledTypography>
          </Button>
        )}
      </StyledBox>
    </StyledFlexContainer>
  </StyledPaper>
);

const ButtonSpacer = styled.div`
  display: inline-block;
  width: 2%;
`;

const StyledImage = styled.img`
  // width: 35%;
  height: 15rem;
`;

const StyledFlexContainer = styled(FlexContainer)`
  padding: ${(props) => props.padding ?? '2.5rem'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const NoGraphData = ({ title, subTitle, isTimelineGraph = false }) => (
  <StyledFlexContainer>
    {isTimelineGraph ? (
      <StyledTimelineGraphIcon color={theme.palette.icons.grey2} />
    ) : (
      <StyledBarGraphIcon color={theme.palette.icons.grey2} />
    )}
    <Typography7 topGutter="1.5" weight={600}>
      {title}
    </Typography7>
    <Typography2 topGutter="1" color={theme.palette.grey.dark}>
      {subTitle}
    </Typography2>
  </StyledFlexContainer>
);
const StyledBarGraphIcon = styled(BarGraphIcon)`
  svg {
    height: 92px;
    width: 92px;
  }
`;
const StyledTimelineGraphIcon = styled(TimelineGraphIcon)`
  svg {
    height: 92px;
    width: 92px;
  }
`;

const ColoredIconButton = styled(StyledIconButton)`
  position: relative;
  background: ${(props) => (props.active ? props.theme.palette.icons.blue : 'transparent')};
  border: 1px solid
    ${(props) => (props.disabled ? props.theme.palette.text.lightDisabled : props.theme.palette.icons.blue)};

  &:hover {
    background: ${(props) => (props.active ? props.theme.palette.icons.blue : 'transparent')};
  }
`;
const StyledColoredCircularProgress = styled(ColoredCircularProgress)`
  position: absolute;
  width: 24px;
  height: 24px;
`;

export const DownloadCSVButton = ({ isOpen, isFetching, hasError, downloadBlob, disabled = false }) => (
  <VerticallyAlignedFlexContainer style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
    <StyledTypography
      variant="body1"
      color={disabled ? theme.palette.text.lightDisabled : 'textSecondary'}
      rightGutter={2}
    >
      Download CSV
    </StyledTypography>
    <ColoredIconButton active={isOpen} size="fixedSmallest" disabled={disabled}>
      {isFetching ? (
        <>
          <StyledColoredCircularProgress color={isOpen ? 'white' : 'blue'} size="" />
          <StyledHourGlassIcon size="tiniest" color={isOpen ? 'white' : 'blue'} />
        </>
      ) : (
        <>
          {' '}
          {!hasError && !downloadBlob ? (
            <DownloadArrowIcon
              size="smaller"
              color={disabled ? theme.palette.text.lightDisabled : isOpen ? 'white' : 'blue'}
            />
          ) : (
            <>
              {!hasError && downloadBlob ? (
                <CheckIcon style={{ margin: '0 auto' }} size="tiniest" color={isOpen ? 'white' : 'blue'} />
              ) : (
                <StyledIcon size="tiniest" color={isOpen ? 'white' : 'blue'}>
                  <PriorityHighIcon />
                </StyledIcon>
              )}
            </>
          )}
        </>
      )}
    </ColoredIconButton>
  </VerticallyAlignedFlexContainer>
);

export const OKRMenuOptions = ({
  addObjective = () => {},
  addKR = () => {},
  isOpen = false,
  anchorEl,
  setIsOpen = () => {},
  isSubObjectiveMenu = false,
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { objectiveCreationAccess } = useSelector(selectOrgData);

  const showObjectiveOption = () => {
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[0].value) {
      return checkUserIsGoalAdmin();
    }
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[1].value) {
      return checkUserIsGoalAdmin() || loggedInEmployee?.isHRISManager;
    }

    return false;
  };

  const handleClose = (e, option) => {
    switch (option) {
      case 'ADD_OBJECTIVE':
        addObjective();
        break;
      case 'ADD_KR':
        addKR();
        break;
      default:
        break;
    }
  };

  return (
    <StyledMenu
      anchorEl={anchorEl?.current}
      keepMounted
      open={isOpen}
      onClose={handleClose}
      disablePortal
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'top',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'top',
      }}
    >
      {showObjectiveOption() && (
        <StyledMenuItem onClick={(e) => handleClose(e, 'ADD_OBJECTIVE')}>
          <ObjectiveSvgIcon
            size="fixed12px"
            color={theme.palette.icons.dark}
            style={{ marginLeft: '2px', marginRight: '2px' }}
          />
          Add New {objectivesName}
        </StyledMenuItem>
      )}
      <StyledMenuItem onClick={(e) => handleClose(e, 'ADD_KR')}>
        <KrSvgIcon
          size="fixed12px"
          color={theme.palette.icons.dark}
          style={{ marginLeft: '2px', marginRight: '2px' }}
        />
        Add New {keyResultsName}
      </StyledMenuItem>
    </StyledMenu>
  );
};

const StyledMenu = styled(Menu)`
  ul {
    padding-top: 10px;
    padding-bottom: 10px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding-right: 30px;
  svg {
    font-size: 1rem;
    margin-right: 0.85rem;
    margin-left: 0.25rem;
    color: #333;
  }
`;

export const RealignHoverContainer = ({
  children,
  hoverComponent,
  clickable = true,
  handleMouseEnter = () => {},
  handleMouseLeave = () => {},
  handleHoverMouseEnter = () => {},
  handleHoverMouseLeave = () => {},
  pointerLocation,
  isObjectiveRealign = false,
}) => {
  const componentElem = useRef(null);

  const childrenWithProps = React.Children.map(children, (child, index) =>
    React.cloneElement(child, {
      ref: componentElem,
    })
  );

  return (
    <>
      <HoverContainer
        clickable={clickable}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        active={pointerLocation.elem || pointerLocation.hover}
      >
        {childrenWithProps}
      </HoverContainer>
      <Popper
        open={pointerLocation.elem || pointerLocation.hover}
        placement="right"
        anchorEl={componentElem.current}
        transition
        style={{ zIndex: 5 }}
        modifiers={{
          offset: {
            enabled: true,
            offset: isObjectiveRealign ? '10,20' : '0,20',
          },
        }}
      >
        <StyledPaper
          elevation={0}
          onMouseEnter={handleHoverMouseEnter}
          onMouseLeave={handleHoverMouseLeave}
          style={{ borderRadius: '50%' }}
        >
          {hoverComponent}
        </StyledPaper>
      </Popper>
    </>
  );
};

const HoverContainer = styled.div`
  position: absolute;
  top: -12px;
  left: -32px;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
`;
